import { computed, inject } from '@angular/core';
import {
  signalStore,
  withComputed,
  withMethods,
  withProps,
} from '@ngrx/signals';
import { withLogger } from '@ngrx-traits/signals';
import {
  CaseSidebarPageKey,
  CASE_SIDEBAR_PAGES,
} from '../models/navigation-config.model';
import { DefaultNavigationConfigStore } from './default-navigation-config.store';
import { CurrentNavigationConfigStore } from './current-navigation-config.store';

export const NavigationConfigStore = signalStore(
  withLogger({
    name: 'NavigationConfigStore',
  }),
  withProps(() => ({
    _defaultStore: inject(DefaultNavigationConfigStore),
    _currentStore: inject(CurrentNavigationConfigStore),
  })),
  withComputed(({ _defaultStore, _currentStore }) => ({
    defaultNavEntities: computed(() => _defaultStore.defaultNavEntities()),
    currentNavEntities: computed(() => _currentStore.currentNavEntities()),
    defaultNavCallStatus: computed(() => _defaultStore.defaultNavCallStatus()),
    currentNavCallStatus: computed(() => _currentStore.currentNavCallStatus()),
    activeConfig: computed(() => {
      const current = _currentStore.currentNavEntities();
      return current.length > 0 ? current : _defaultStore.defaultNavEntities();
    }),
    usingTenantConfig: computed(
      () => _currentStore.currentNavEntities().length > 0
    ),
    pageVisibility: computed(() => {
      const current = _currentStore.currentNavEntities();
      const config =
        current.length > 0 ? current : _defaultStore.defaultNavEntities();

      if (config.length === 0) {
        return {} as Record<CaseSidebarPageKey, boolean>;
      }

      return config.reduce(
        (acc, item) => ({ ...acc, [item.name]: item.isVisible }),
        {} as Record<CaseSidebarPageKey, boolean>
      );
    }),
    orderedPageKeys: computed(() => {
      const current = _currentStore.currentNavEntities();
      const config =
        current.length > 0 ? current : _defaultStore.defaultNavEntities();

      if (config.length === 0) {
        return [];
      }

      return [...config]
        .sort((a, b) => a.orderPosition - b.orderPosition)
        .map((item) => item.name);
    }),
    allPagesWithVisibility: computed(() => {
      const current = _currentStore.currentNavEntities();
      const config =
        current.length > 0 ? current : _defaultStore.defaultNavEntities();

      if (config.length === 0) {
        return [];
      }

      const sortedConfig = [...config].sort(
        (a, b) => a.orderPosition - b.orderPosition
      );

      return sortedConfig.map((item) => {
        const pageInfo = CASE_SIDEBAR_PAGES.find((p) => p.key === item.name);
        return {
          key: item.name,
          label: pageInfo?.label ?? item.name,
          description: pageInfo?.description,
          visible: item.isVisible,
          orderPosition: item.orderPosition,
          id: item.id,
        };
      });
    }),
    visiblePagesCount: computed(() => {
      const current = _currentStore.currentNavEntities();
      const config =
        current.length > 0 ? current : _defaultStore.defaultNavEntities();

      if (config.length === 0) {
        return 0;
      }
      return config.filter((item) => item.isVisible).length;
    }),
    totalPagesCount: computed(() => CASE_SIDEBAR_PAGES.length),
    isLoaded: computed(
      () =>
        _defaultStore.defaultNavCallStatus() === 'loaded' &&
        _currentStore.currentNavCallStatus() === 'loaded'
    ),
  }))
);
